import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { filter, finalize, switchMap, take } from 'rxjs/operators';
import { fadeAnimation, showAnimation } from 'src/animations';
import { modules } from 'src/app/globals';
import { Issue, IssueItem } from 'src/app/lib/issue/classes';
import { CreateIssueDialogComponent, CreateIssueDialogComponentInputData } from 'src/app/lib/issue/components/dialog/create-issue-dialog/create-issue-dialog.component';
import { ItemAssistDialogOutput, ItemAssistentDialogComponent } from 'src/app/lib/issue/components/dialog/item-assistent-dialog/item-assistent-dialog.component';
import { IssueService } from 'src/app/lib/issue/services/issue.service';
import { Address } from 'src/app/lib/location/classes';
import { SidenavOption } from 'src/app/lib/shared/classes';
import { CompleteRequestDialogComponent, CompleteRequestDialogComponentInputData } from 'src/app/lib/shared/components/dialog/complete-request-dialog/complete-request-dialog.component';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { HereMapDialogComponent, HereMapDialogComponentData } from 'src/app/lib/shared/components/dialog/here-map-dialog/here-map-dialog.component';
import { WaitComponent } from 'src/app/lib/shared/components/dialog/wait/wait.component';
import { IssueTypes } from 'src/app/lib/shared/globals';
import { CONCURRENCY_PROVIDER_SERVICE, OFFER_PROVIDER_SERVICE } from 'src/app/lib/shared/interfaces';
import { ReportService } from 'src/app/lib/shared/services/report.service';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { SidenavService } from 'src/app/lib/shared/services/sidenav.service';
import { Offer, OfferItem } from '../../../classes';
import { OfferDetailsComponentService } from './offer-details-component-service';

@Component({
  selector: 'app-offer-details',
  templateUrl: './offer-details.component.html',
  styleUrls: ['./offer-details.component.css'],
  providers: [
    SidenavService,
    { provide: OFFER_PROVIDER_SERVICE, useClass: OfferDetailsComponentService },
    { provide: CONCURRENCY_PROVIDER_SERVICE, useExisting: OFFER_PROVIDER_SERVICE }
  ],
  animations: [showAnimation, fadeAnimation]
})
export class OfferDetailsComponent implements OnDestroy, OnInit {

  appearance: 'standard' | 'outline' | 'fill' = 'outline';

  disabled: boolean = false;
  reset: boolean = false;
  itemsRefreshing: boolean = false;

  itemColumns = ['position', 'itemselected', 'term', 'count', 'product', 'description', 'info', 'setupcost', 'rentalcost', 'purchasecost', 'controls'];

  formGroup: FormGroup;

  rightsMap = {
    "delete_offers": false
  };

  private _sidenavOptions = {
    'editMode': {
      'icon': () => this.disabled ? 'edit' : 'edit_off',
      'title': () => this.disabled ? 'Bearbeiten' : 'Sperren',
      'onClick': () => of(this.toggleEditMode()),
      'visible': () => true,
      'appearance': () => 'default'
    } as SidenavOption,
    'save': {
      'icon': () => this.formGroup.dirty ? 'save' : 'done',
      'title': () => this.formGroup.dirty ? 'Speichern' : 'Aktuell',
      'onClick': () => this.formGroup.dirty ? this.saveChanges() : of(null),
      'visible': () => !this.disabled,
      'appearance': () => this.formGroup.dirty ? 'success' : 'ok'
    } as SidenavOption,
    'discard': {
      'icon': () => 'clear',
      'title': () => 'Verwerfen',
      'onClick': () => this.discardChanges(),
      'visible': () => !this.disabled && this.formGroup.dirty,
      'appearance': () => 'danger'
    } as SidenavOption,
    'pdf': {
      'icon': () => 'picture_as_pdf',
      'title': () => 'Angebot PDF',
      'onClick': () => this.generateReportInNewWindow(),
      'visible': () => this.formGroup.pristine,
      'appearance': () => 'primary'
    } as SidenavOption,
    'issue': {
      'icon': () => 'read_more',
      'title': () => 'Zu Vorgang',
      'onClick': () => this.convertToIssue(),
      'visible': () => this.formGroup.pristine && !this.disabled,
      'appearance': () => 'primary'
    } as SidenavOption,
    'delete': {
      'icon': () => 'delete_forever',
      'title': () => 'Löschen',
      'onClick': () => this.deleteOffer(),
      'visible': () => !this.disabled && this.rightsMap.delete_offers,
      'appearance': () => 'danger'
    } as SidenavOption
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(OFFER_PROVIDER_SERVICE) public offerDetailsComponentService: OfferDetailsComponentService,
    private _formBuilder: FormBuilder,
    private _sidenavService: SidenavService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _reportService: ReportService,
    private _sessionService: SessionService,
    private _router: Router,
    private _issueService: IssueService,
    private _changeRef: ChangeDetectorRef
  ) { }

  addArticle() {
    let group = this._formBuilder.group(new OfferItem());
    this.itemControl.push(group);
    group.markAsDirty();
  };

  afterStoring() {
    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();
    this.offerDetailsComponentService.refreshCurrentOffer();
    this._snackBar.open('Änderungen erfolgreich gespeichert!', 'Speichern', {
      duration: 2000
    });
  }

  clearTitle() {
    this.titleControl.setValue(null);
    this.titleControl.markAsDirty();
  }

  convertToIssue(): Observable<Object> {
    this.offerDetailsComponentService.offer$.pipe(take(1)).subscribe((offer: Offer) => {
      this._dialog.open(CreateIssueDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          issue: {
            'issuetitle': `Vorgang ${[offer.offernumber, offer.offertitle].filter(x => x? true: false).join(' ')}`,
            'issuecustomer': offer.offercustomer,
            'localcontactpersonalid': offer.localcontact,
            'commercialcontactpersonalid': offer.commercialcontact,
            'issuedesc': `Vorgang, erzeugt am ${moment().format('DD.MM.YYYY HH')} Uhr aus dem Angebot '${[offer.offernumber, offer.offertitle].filter(x => x? true: false).join(' ')}'.`,
            'issuetype': IssueTypes.Issue
          } as Issue,
          afterStoring: (issue: Issue) => {
            (this.formGroup.controls['offer'] as FormGroup).controls['offerissue'].setValue(issue.issueid);
            return this.offerDetailsComponentService.saveChanges(this.formGroup.controls['offer'].value, 'Angebot wurde in Vorgang kopiert')
              .pipe(switchMap((offer: Offer) => {
                let items = (this.itemControl.value as OfferItem[]).filter(x => x.itemselected);
                return items.length === 0? of(null): combineLatest(items.map(x => this._issueService.createIssueItem({
                  'itembuildindate': x.itembuildindate,
                  'itemcanceldate': x.itemcanceldate,
                  'itemcontract': x.itemcontract,
                  'itemcount': x.itemcount,
                  'itemdesc': x.itemdesc,
                  'itemfaktdate': x.itemfaktdate,
                  'iteminfo': x.iteminfo,
                  'itemissue': issue.issueid,
                  'itemname': x.itemterm,
                  'itemposition': x.itemposition,
                  'itemproduct': x.itemproduct,
                  'itempurchasecost': x.itempurchasecost,
                  'itemrentalcost': x.itemrentalcost,
                  'itemsetupcost': x.itemsetupcost
                } as IssueItem, `Vorgangsartikel, erzeugt aus Angebotsartikel ${x.itemid}`)));
              }));
          }
        } as CreateIssueDialogComponentInputData
      });
    });
    return of(null);
  }

  deleteOffer() {
    let subject = new Subject();
    this.offerDetailsComponentService.offer$.pipe(take(1)).subscribe((offer: Offer) => {
      var number = offer && offer.offernumber? offer.offernumber.toString(): (Math.random() * (9000) + 1000).toFixed(0);
      this._dialog.open(DoubleCheckDialogComponent, {
        autoFocus: false,
        panelClass: 'no-padding-dialog-container',
        data: {
          mode: 'numeric',
          title: 'Löschen bestätigen',
          text: `Bitte geben Sie die Nummer ${number} ein, um das Angebot dauerhaft zu löschen`,
          doubleCheckValue: number
        } as DoubleCheckDialogComponentInputData
      }).afterClosed().subscribe((result) => {
        if (result) this.offerDetailsComponentService.deleteCurrentOffer().subscribe(subject);
        else subject.complete();
      });
    });
    return subject.asObservable();
  }

  discardChanges(): Observable<Object> {
    this.reset = true;
    return this.offerDetailsComponentService.refreshCurrentOffer();
  }

  generateReportInNewWindow() {
    let ref = this._dialog.open(WaitComponent, {
      disableClose: true,
      data: {
        'headline': 'Generiere Datei'
      }
    });
    this._reportService.getOfferReport(this.idControl.value).subscribe({
      next: (result: Blob) => {
        window.open(URL.createObjectURL(result));
        ref.close();
      },
      error: (error: HttpErrorResponse) => {
        this._snackBar.open(`Das Angebot konnte nicht erstellt werden da ein Fehler aufgetreten ist. (${error.message})`, "Download Angebot", {
          'duration': 6000
        });
        ref.close();
      }
    });
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) {
      sub.unsubscribe();
      this._subscriptions = [];
    }
    this.offerDetailsComponentService.dispose();
  }

  ngOnInit(): void {

    this.initRights();

    this.formGroup = this._formBuilder.group({
      offer: this._formBuilder.group(new Offer()),
      billingaddress: this._formBuilder.group(new Address()),
      serviceaddress: this._formBuilder.group(new Address()),
      items: this._formBuilder.array([])
    });

    this._subscriptions.push(...[
      this.offerDetailsComponentService.offer$.pipe(take(1), filter(x => this.formGroup.pristine || this.reset)).subscribe((offer: Offer) => this.setOffer(offer))
    ]);

    this._sidenavService.setOptions(Object.values(this._sidenavOptions));

    this.setValidators();

  }

  openItemAssist = () => {
    this._dialog.open(ItemAssistentDialogComponent, {
      panelClass: 'no-padding-dialog-container'
    }).afterClosed().subscribe((result: ItemAssistDialogOutput) => {
      let max = Math.max(...this.itemControl.value.map(x => x.itemposition), 0);
      for (let item of result.items) {
        max++;
        this.itemControl.push(this._formBuilder.group({
          itemid: null,
          itembuildindate: item.itembuildindate,
          itemcanceldate: item.itemcanceldate,
          itemcount: 1,
          itemfaktdate: item.itemfaktdate,
          iteminfo: null,
          itemposition: max,
          itemproduct: item.itemproduct,
          itempurchasecost: item.itempurchasecost,
          itemrentalcost: item.itemrentalcost,
          itemsetupcost: item.itemsetupcost,
          itemissue: this.issueControl.value,
          itemselected: false,
          itemterm: item.itemservice,
          itemlocation: null,
          itemoffer: null,
          offer: null
        } as OfferItem));
      }
      this.itemControl.markAsDirty();
    });
  };

  resetOfferItems(offer: Offer) {
    this.itemsRefreshing = true;
    timer(100).subscribe(() => {
      this.itemControl.clear();
      offer.offeritem.forEach(x => {
        let group = this._formBuilder.group(x);
        this.itemControl.push(group);
        if (this.disabled) group.disable();
      });
      this.itemsRefreshing = false;
    });
  }

  saveChanges(): Observable<any> {
    let subject = new Subject();

    combineLatest([
      this._dialog.open(CompleteRequestDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          addNote: true
        } as CompleteRequestDialogComponentInputData
      }).afterClosed(),
      this.offerDetailsComponentService.offer$
    ]).pipe(take(1))
      .subscribe(([output, offer]) => {
        if (output.abort) {
          subject.complete();
          return;
        }

        let formItemIds = (this.itemControl.value as OfferItem[]).map(x => x.itemid);
        this.offerDetailsComponentService.saveChanges(
          (this.formGroup.controls['offer'] as FormGroup).getRawValue(),
          output.note,
          this.billingAddressControl.getRawValue(),
          this.serviceAddressControl.getRawValue(),
          Object.values((this.itemControl as FormArray).controls).filter(x => x.dirty || x.value.itemid === null).map((x: FormGroup) => x.getRawValue()),
          offer.offeritem.map(x => x.itemid).filter(x => formItemIds.indexOf(x) === -1)
        ).pipe(finalize(() => this.afterStoring()))
          .subscribe(subject);

      });

    return subject.asObservable();
  }

  setOffer(offer: Offer) {
    this.reset = false;
    this.formGroup.controls['offer'].patchValue(offer);
    this.billingAddressControl.patchValue(offer.billingaddress);
    this.serviceAddressControl.patchValue(offer.serviceaddress);
    this.formGroup.markAsPristine();
    this.resetOfferItems(offer);
    if(offer.offerissue){
      this.formGroup.disable({ emitEvent: false });
      this.disabled = true;
      this._changeRef.detectChanges();
      timer(200).subscribe(() => this.formGroup.markAsPristine());
    }
  }

  setValidators() {
    this.billingAddressControl.controls['addressstreet'].setValidators(Validators.required);
    this.serviceAddressControl.controls['addressstreet'].setValidators(Validators.required);
  }

  showAddress = (address: Address) => this._dialog.open(HereMapDialogComponent, {
    panelClass: 'no-padding-dialog-container',
    data: { address: address } as HereMapDialogComponentData
  });
  
  showBillingAddress = () => this.showAddress(this.billingAddressControl.value);
  showServiceAddress = () => this.showAddress(this.serviceAddressControl.value);

  showIssue(){
    if(this.issueControl.value) this._router.navigate(['/issue-details', this.issueControl.value]);
  }

  toggleEditMode() {
    let pristine = this.formGroup.pristine;
    this.formGroup[this.disabled ? 'enable' : 'disable']({ emitEvent: false });
    this.disabled = !this.disabled;
    if (pristine) this.formGroup.markAsPristine();
  }

  @HostListener('keydown', ['$event'])
  private _keydown(event: KeyboardEvent){
    if(event.ctrlKey && event.key === 's'){
      event.preventDefault();
      event.stopPropagation();
      if(this.formGroup.dirty) this.saveChanges();
      else this._snackBar.open('Keine Änderungen zum Speichern erkannt', 'Ok', {
        duration: 2000
      });
    }
  }

  modules = modules;

  get billingAddressControl(): FormGroup {
    return this.formGroup.controls['billingaddress'] as FormGroup;
  }
  get customerControl(): AbstractControl {
    return (this.formGroup.controls['offer'] as FormGroup).controls['offercustomer'];
  }
  get descriptionControl(): AbstractControl {
    return (this.formGroup.controls['offer'] as FormGroup).controls['offerdesc'];
  }
  get idControl(): AbstractControl {
    return (this.formGroup.controls['offer'] as FormGroup).controls['offerid'];
  }
  get issueControl(): AbstractControl {
    return (this.formGroup.controls['offer'] as FormGroup).controls['offerissue'];
  }
  get itemControl(): FormArray {
    return (this.formGroup.controls['items'] as FormArray);
  }
  get numberControl(): AbstractControl {
    return (this.formGroup.controls['offer'] as FormGroup).controls['offernumber'];
  }
  get serviceAddressControl(): FormGroup {
    return this.formGroup.controls['serviceaddress'] as FormGroup;
  }
  get titleControl(): AbstractControl {
    return (this.formGroup.controls['offer'] as FormGroup).controls['offertitle'];
  }

}
